import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const BookNow = () => {
  const [selectedPackage, setSelectedPackage] = useState('scale');
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    preferredAreas: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const packages = {
    single: {
      name: 'Single Walkthrough',
      price: '$200',
      description: 'per appointment',
      features: [
        'Pre-qualified decision maker',
        'Confirmed appointment time',
        'Building details provided',
        'Square footage information',
        'Contact information included',
      ],
    },
    scale: {
      name: 'The Scale Pack',
      price: '$1,500',
      description: '10 appointments',
      savings: 'Save $500',
      features: [
        'Everything in Single Walkthrough',
        '10 pre-set appointments',
        'Priority scheduling',
        'Dedicated account support',
        'Flexible delivery timeline',
      ],
    },
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    await new Promise((resolve) => setTimeout(resolve, 1500));

    console.log('Booking Request:', { package: selectedPackage, ...formData });
    setIsSubmitting(false);
    setIsSubmitted(true);
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-cleaning-blue-50/30 to-fresh-white pt-24 pb-16">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-white rounded-3xl shadow-2xl shadow-slate-200/50 p-12 border border-slate-100">
              <div className="w-20 h-20 bg-gradient-to-br from-action-green-400 to-action-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-3xl font-bold text-slate-900 mb-4">Request Received!</h1>
              <p className="text-lg text-slate-600 mb-4">
                Thank you for choosing Squeakyleads! We've received your request for the{' '}
                <span className="font-semibold">{packages[selectedPackage].name}</span>.
              </p>
              <p className="text-slate-500 mb-8">
                Our team will reach out within 24 hours to confirm your order and discuss your preferred service areas.
              </p>
              <Link to="/" className="btn-primary">
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-cleaning-blue-50/30 to-fresh-white pt-24 pb-16">
      {/* Background Elements */}
      <div className="fixed inset-0 bg-grid-pattern opacity-30 pointer-events-none" />
      <div className="fixed top-20 left-0 w-96 h-96 bg-cleaning-blue-200 rounded-full filter blur-[150px] opacity-30 pointer-events-none" />
      <div className="fixed bottom-0 right-0 w-96 h-96 bg-action-green-200 rounded-full filter blur-[150px] opacity-30 pointer-events-none" />

      <div className="container-custom relative z-10">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <Link to="/" className="inline-flex items-center gap-2 text-slate-500 hover:text-cleaning-blue-600 mb-6 transition-colors">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Home
            </Link>
            <span className="inline-block px-4 py-2 bg-cleaning-blue-100 text-cleaning-blue-700 rounded-full text-sm font-semibold mb-4">
              Buy Leads
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              Book Your
              <span className="gradient-text"> Walkthroughs</span>
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Choose your package and we'll start setting up appointments with decision-makers in your area.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Package Selection */}
            <div>
              <h2 className="text-lg font-bold text-slate-900 mb-4">1. Choose Your Package</h2>
              <div className="space-y-4">
                {/* Single Package */}
                <label
                  className={`block cursor-pointer group ${
                    selectedPackage === 'single' ? 'ring-2 ring-cleaning-blue-500' : ''
                  }`}
                >
                  <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/50 p-6 border border-slate-100 hover:border-cleaning-blue-300 transition-all duration-200">
                    <div className="flex items-start gap-4">
                      <div className="relative flex items-center">
                        <input
                          type="radio"
                          name="package"
                          value="single"
                          checked={selectedPackage === 'single'}
                          onChange={() => setSelectedPackage('single')}
                          className="sr-only peer"
                        />
                        <div className="w-6 h-6 border-2 border-slate-300 rounded-full peer-checked:border-cleaning-blue-500 peer-checked:bg-cleaning-blue-500 transition-all duration-200 flex items-center justify-center">
                          <div className={`w-2.5 h-2.5 bg-white rounded-full transition-opacity duration-200 ${selectedPackage === 'single' ? 'opacity-100' : 'opacity-0'}`} />
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-xl font-bold text-slate-900">{packages.single.name}</h3>
                          <div className="text-right">
                            <span className="text-2xl font-bold text-slate-900">{packages.single.price}</span>
                            <span className="text-slate-500 text-sm ml-1">{packages.single.description}</span>
                          </div>
                        </div>
                        <ul className="space-y-2 mt-4">
                          {packages.single.features.slice(0, 3).map((feature, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                              <svg className="w-4 h-4 text-action-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </label>

                {/* Scale Package */}
                <label
                  className={`block cursor-pointer group relative ${
                    selectedPackage === 'scale' ? 'ring-2 ring-action-green-500' : ''
                  }`}
                >
                  <div className="absolute -top-3 left-6 px-3 py-1 bg-action-green-500 text-white text-xs font-bold rounded-full z-10">
                    BEST VALUE
                  </div>
                  <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/50 p-6 border-2 border-action-green-200 hover:border-action-green-400 transition-all duration-200">
                    <div className="flex items-start gap-4">
                      <div className="relative flex items-center">
                        <input
                          type="radio"
                          name="package"
                          value="scale"
                          checked={selectedPackage === 'scale'}
                          onChange={() => setSelectedPackage('scale')}
                          className="sr-only peer"
                        />
                        <div className="w-6 h-6 border-2 border-slate-300 rounded-full peer-checked:border-action-green-500 peer-checked:bg-action-green-500 transition-all duration-200 flex items-center justify-center">
                          <div className={`w-2.5 h-2.5 bg-white rounded-full transition-opacity duration-200 ${selectedPackage === 'scale' ? 'opacity-100' : 'opacity-0'}`} />
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-xl font-bold text-slate-900">{packages.scale.name}</h3>
                          <div className="text-right">
                            <span className="text-2xl font-bold text-slate-900">{packages.scale.price}</span>
                            <span className="text-slate-500 text-sm ml-1">/ {packages.scale.description}</span>
                          </div>
                        </div>
                        <div className="inline-flex items-center gap-1 px-2 py-1 bg-action-green-100 text-action-green-700 rounded-full text-sm font-semibold mb-3">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {packages.scale.savings}
                        </div>
                        <ul className="space-y-2">
                          {packages.scale.features.map((feature, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                              <svg className="w-4 h-4 text-action-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </label>
              </div>

              {/* Trust Elements */}
              <div className="mt-8 bg-slate-50 rounded-2xl p-6">
                <h3 className="font-bold text-slate-900 mb-4">Why Squeakyleads?</h3>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: '✓', title: 'Pre-qualified leads', desc: 'Decision makers only' },
                    { icon: '📅', title: 'Confirmed times', desc: 'No more no-shows' },
                    { icon: '📍', title: 'Your service area', desc: 'Local appointments' },
                    { icon: '💯', title: '100% focused', desc: 'On your success' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className="text-lg">{item.icon}</span>
                      <div>
                        <p className="font-medium text-slate-800 text-sm">{item.title}</p>
                        <p className="text-xs text-slate-500">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Booking Form */}
            <div>
              <h2 className="text-lg font-bold text-slate-900 mb-4">2. Your Information</h2>
              <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 p-6 border border-slate-100">
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="companyName" className="block text-sm font-medium text-slate-700 mb-2">
                        Company Name *
                      </label>
                      <input
                        type="text"
                        id="companyName"
                        name="companyName"
                        required
                        value={formData.companyName}
                        onChange={handleChange}
                        className="input-field"
                        placeholder="Your Cleaning Co."
                      />
                    </div>
                    <div>
                      <label htmlFor="contactName" className="block text-sm font-medium text-slate-700 mb-2">
                        Contact Name *
                      </label>
                      <input
                        type="text"
                        id="contactName"
                        name="contactName"
                        required
                        value={formData.contactName}
                        onChange={handleChange}
                        className="input-field"
                        placeholder="John Smith"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="input-field"
                        placeholder="squeakyleads@gmail.com"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        className="input-field"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="preferredAreas" className="block text-sm font-medium text-slate-700 mb-2">
                      Preferred Service Areas *
                    </label>
                    <input
                      type="text"
                      id="preferredAreas"
                      name="preferredAreas"
                      required
                      value={formData.preferredAreas}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="Atlanta, GA or 30301, 30302..."
                    />
                    <p className="text-xs text-slate-500 mt-1">Cities, zip codes, or regions where you can service</p>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                      Additional Notes (optional)
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={3}
                      value={formData.message}
                      onChange={handleChange}
                      className="input-field resize-none"
                      placeholder="Any specific preferences or requirements..."
                    />
                  </div>

                  {/* Order Summary */}
                  <div className="bg-slate-50 rounded-xl p-4 mt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-slate-600">Selected Package</span>
                      <span className="font-semibold text-slate-900">{packages[selectedPackage].name}</span>
                    </div>
                    <div className="flex items-center justify-between text-lg">
                      <span className="font-bold text-slate-900">Total</span>
                      <span className="font-bold text-action-green-600">{packages[selectedPackage].price}</span>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full justify-center text-lg py-4 mt-4 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Processing...
                      </>
                    ) : (
                      <>
                        Complete Booking
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </>
                    )}
                  </button>

                  <p className="text-center text-xs text-slate-500 mt-3">
                    You'll receive a confirmation email with payment instructions
                  </p>
                </div>
              </form>
            </div>
          </div>

          {/* Terms & Refund Policy Section */}
          <div className="max-w-5xl mx-auto mt-12">
            <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6 md:p-8">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
                <svg className="w-6 h-6 text-cleaning-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Service Details & Terms of Agreement
              </h3>

              <ul className="space-y-4 text-slate-300">
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-cleaning-blue-400 rounded-full mt-2 flex-shrink-0" />
                  <span>Please allow up to <strong className="text-white">60 days</strong> for your lead to be delivered (normally less than 30 days for an order of 10 leads).</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-cleaning-blue-400 rounded-full mt-2 flex-shrink-0" />
                  <span>If appointments are not emailed by your deadline date, you will be <strong className="text-white">refunded</strong> or we will continue to look for leads on your behalf.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-cleaning-blue-400 rounded-full mt-2 flex-shrink-0" />
                  <span>Appointments are set around client availability (typically <strong className="text-white">Mon–Fri before noon</strong>).</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-cleaning-blue-400 rounded-full mt-2 flex-shrink-0" />
                  <span>You are responsible for closing deals; we provide <strong className="text-white">1-on-1 coaching and courses</strong> for sales/management training.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-cleaning-blue-400 rounded-full mt-2 flex-shrink-0" />
                  <span>SqueakyLeads is <strong className="text-white">not responsible</strong> for missed appointments or notifications sent to spam.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-cleaning-blue-400 rounded-full mt-2 flex-shrink-0" />
                  <span>All leads have expressed interest in a cleaning quote.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-cleaning-blue-400 rounded-full mt-2 flex-shrink-0" />
                  <span>Leads cover any commercial niche (<strong className="text-white">Medical, Retail, Law, etc.</strong>) and any service (<strong className="text-white">Strip/Wax, Deep Clean, etc.</strong>).</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-cleaning-blue-400 rounded-full mt-2 flex-shrink-0" />
                  <span>Invalid leads will be <strong className="text-white">replaced</strong> if reported before the deadline.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-red-300"><strong className="text-red-200">NO REFUNDS</strong> will be provided unless these specific terms are not met.</span>
                </li>
              </ul>

              {/* Contact Support Button */}
              <div className="mt-8 pt-6 border-t border-slate-700">
                <a
                  href="mailto:squeakyleads@gmail.com?subject=Refund%20Request%3A%20%5BInsert%20Company%20Name%5D"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-transparent border-2 border-slate-500 text-slate-300 font-semibold rounded-xl hover:border-cleaning-blue-400 hover:text-cleaning-blue-400 hover:bg-slate-700/50 active:scale-[0.98] transition-all duration-200"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Contact Support for Refund
                </a>
              </div>

              {/* Acknowledgment */}
              <p className="mt-6 text-xs text-slate-500 leading-relaxed">
                By submitting the form above, you acknowledge that you have read these Details and agree to the terms provided.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookNow;
